import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  Container,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useAuth } from "../../contexts/AuthContext";
import userManagementApi from "../../services/user-management-api";
import { Employee } from "../../types/leave";
import { format, parse } from "date-fns";

// Helper function to parse various date formats
const parseDate = (dateValue: any): Date | null => {
  if (!dateValue) return null;

  try {
    // Handle array format [year, month, day] from backend
    if (Array.isArray(dateValue)) {
      if (dateValue.length >= 3) {
        const [year, month, day] = dateValue;
        return new Date(year, month - 1, day);
      }
      return null;
    }

    const dateString = String(dateValue);

    // Try ISO format with T (2024-10-18T00:00:00)
    if (dateString.includes("T")) {
      return new Date(dateString);
    }

    // Try YYYY-MM-DD format
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return parse(dateString, "yyyy-MM-dd", new Date());
    }

    // Try MM/DD/YYYY format
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return parse(dateString, "MM/dd/yyyy", new Date());
    }

    // Try DD/MM/YYYY format
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return parse(dateString, "dd/MM/yyyy", new Date());
    }

    // Fallback: try to parse as Date
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date;
    }

    return null;
  } catch {
    return null;
  }
};

// Helper function to format date for display
const formatDateForDisplay = (
  dateString: string | null | undefined,
): string => {
  if (!dateString) return "-";

  const date = parseDate(dateString);
  if (!date) return dateString || "-";

  try {
    return format(date, "MMM dd, yyyy");
  } catch {
    return dateString || "-";
  }
};

// Helper function to convert date to YYYY-MM-DD format for input
const formatDateForInput = (dateString: string | null | undefined): string => {
  if (!dateString) return "";

  const date = parseDate(dateString);
  if (!date) return "";

  try {
    return format(date, "yyyy-MM-dd");
  } catch {
    return "";
  }
};

// Helper component for read-only field display
const ReadOnlyField: React.FC<{
  label: string;
  value: string | null | undefined;
  isDate?: boolean;
}> = ({ label, value, isDate = false }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
      {isDate ? formatDateForDisplay(value) : value || "-"}
    </Typography>
  </Box>
);

const UserProfile: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [profile, setProfile] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<Employee>>({});

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      if (user?.id) {
        // Fetch full employee profile from backend using user-management API
        // This endpoint returns complete profile data including personal and employment information
        const employeeProfile = await userManagementApi.getMyProfile();
        setProfile(employeeProfile);
        setFormData(employeeProfile);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("error.failedToLoadProfile"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = useCallback((field: keyof Employee, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleSave = async () => {
    try {
      if (user?.id) {
        await userManagementApi.updateUserProfile(user.id, formData);
        setSuccess(t("profile.updateSuccess"));
        setIsEditing(false);
        loadProfile();
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("error.failedToUpdateProfile"),
      );
    }
  };

  const handleCancel = () => {
    setFormData(profile || {});
    setIsEditing(false);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t("profile.notFound")}</Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">{t("profile.title")}</Typography>

        {!isEditing && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setIsEditing(true)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            {t("profile.edit")}
          </Button>
        )}
        {/* Action Buttons */}
        {isEditing && (
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {t("common.save")}
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Personal Information Card - Includes Contact & Emergency Info */}
      <Card
        sx={{ mb: 3, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Personal Information Section */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            👤 {t("profile.personalInfo")}
          </Typography>

          {isEditing ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t("users.firstName")}
                  value={formData.firstName || ""}
                  onChange={(e) =>
                    handleFormChange("firstName", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t("users.middleName")}
                  value={formData.middleName || ""}
                  onChange={(e) =>
                    handleFormChange("middleName", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t("users.lastName")}
                  value={formData.lastName || ""}
                  onChange={(e) => handleFormChange("lastName", e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t("users.email")}
                  type="email"
                  value={formData.email || ""}
                  onChange={(e) => handleFormChange("email", e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t("users.birthDate")}
                  type="date"
                  value={formatDateForInput(formData.birthDate)}
                  onChange={(e) =>
                    handleFormChange("birthDate", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t("users.homeAddress")}
                  value={formData.homeAddress || ""}
                  onChange={(e) =>
                    handleFormChange("homeAddress", e.target.value)
                  }
                  helperText={t("users.homeAddressHelperText")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ReadOnlyField
                  label={t("users.firstName")}
                  value={formData.firstName}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ReadOnlyField
                  label={t("users.middleName")}
                  value={formData.middleName}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ReadOnlyField
                  label={t("users.lastName")}
                  value={formData.lastName}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ReadOnlyField
                  label={t("users.email")}
                  value={formData.email}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ReadOnlyField
                  label={t("users.birthDate")}
                  value={formData.birthDate}
                  isDate={true}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ReadOnlyField
                  label={t("users.homeAddress")}
                  value={formData.homeAddress}
                />
              </Grid>
            </Grid>
          )}

          {/* Contact Information Section */}
          <Divider sx={{ my: 3 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            📞 {t("profile.contactInfo")}
          </Typography>

          {isEditing ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t("users.personalPhone")}
                  type="tel"
                  value={formData.personalPhone || ""}
                  onChange={(e) =>
                    handleFormChange("personalPhone", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t("users.secondaryPhone")}
                  type="tel"
                  value={formData.secondaryPhone || ""}
                  onChange={(e) =>
                    handleFormChange("secondaryPhone", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ReadOnlyField
                  label={t("users.personalPhone")}
                  value={formData.personalPhone}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ReadOnlyField
                  label={t("users.secondaryPhone")}
                  value={formData.secondaryPhone}
                />
              </Grid>
            </Grid>
          )}

          {/* Emergency Contact 1 Section */}
          <Divider sx={{ my: 3 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            🆘 {t("profile.emergencyContact1")}
          </Typography>

          {isEditing ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label={t("users.emergencyContactName")}
                  value={formData.emergencyContactName || ""}
                  onChange={(e) =>
                    handleFormChange("emergencyContactName", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label={t("users.emergencyContactRelationship")}
                  value={formData.emergencyContactRelationship || ""}
                  onChange={(e) =>
                    handleFormChange(
                      "emergencyContactRelationship",
                      e.target.value,
                    )
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label={t("users.emergencyContactPhone")}
                  type="tel"
                  value={formData.emergencyContactPhone || ""}
                  onChange={(e) =>
                    handleFormChange("emergencyContactPhone", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <ReadOnlyField
                  label={t("users.emergencyContactName")}
                  value={formData.emergencyContactName}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <ReadOnlyField
                  label={t("users.emergencyContactRelationship")}
                  value={formData.emergencyContactRelationship}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <ReadOnlyField
                  label={t("users.emergencyContactPhone")}
                  value={formData.emergencyContactPhone}
                />
              </Grid>
            </Grid>
          )}

          {/* Emergency Contact 2 Section */}
          <Divider sx={{ my: 3 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            🆘 {t("profile.emergencyContact2")}
          </Typography>

          {isEditing ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label={t("users.emergencyContactName2")}
                  value={formData.emergencyContactName2 || ""}
                  onChange={(e) =>
                    handleFormChange("emergencyContactName2", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label={t("users.emergencyContactRelationship2")}
                  value={formData.emergencyContactRelationship2 || ""}
                  onChange={(e) =>
                    handleFormChange(
                      "emergencyContactRelationship2",
                      e.target.value,
                    )
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label={t("users.emergencyContactPhone2")}
                  type="tel"
                  value={formData.emergencyContactPhone2 || ""}
                  onChange={(e) =>
                    handleFormChange("emergencyContactPhone2", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <ReadOnlyField
                  label={t("users.emergencyContactName2")}
                  value={formData.emergencyContactName2}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <ReadOnlyField
                  label={t("users.emergencyContactRelationship2")}
                  value={formData.emergencyContactRelationship2}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <ReadOnlyField
                  label={t("users.emergencyContactPhone2")}
                  value={formData.emergencyContactPhone2}
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Employment Information Card - Always Read-Only */}
      <Card
        sx={{ mb: 3, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            💼 {t("profile.employmentInfo")}
          </Typography>

          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReadOnlyField
                label={t("users.username")}
                value={formData.username}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReadOnlyField
                label={t("users.employeeId")}
                value={formData.employeeId}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReadOnlyField
                label={t("users.jobTitle")}
                value={formData.jobTitle}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReadOnlyField
                label={t("users.department")}
                value={formData.department}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReadOnlyField
                label={t("users.officeLocation")}
                value={formData.officeLocation}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReadOnlyField
                label={t("users.hireDate")}
                value={formData.hireDate}
                isDate={true}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReadOnlyField
                label={t("users.startDate")}
                value={formData.startDate}
                isDate={true}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserProfile;
